import React, { createContext, useCallback, useContext, useReducer } from "react";
import type { PropsWithChildren } from "react";
import type { HeatmapData, PlotProps } from ".";

interface DataState {
	plotData: PlotProps[] | null;
	trialPolygon: GeoJSON.Polygon | null;
	heatmapData: HeatmapData[] | null;
	heatmapDataAllDates: HeatmapData[] | null;
	heatmapDataLoading: boolean;
	dataLayerNames: string[];
	dataLayerValues: (number | string)[];
	hideExclusions: boolean;
	showCategoricalValues: boolean;
}

interface DataFunctions {
	updatePlotData: (data: PlotProps[] | null) => void;
	updateHeatMapData: (data: HeatmapData[] | null) => void;
	updateHeatMapDataAllDates: (data: HeatmapData[] | null) => void;
	updateHeatMapDataLoading: (loading: boolean) => void;
	updateTrialPolygon: (polygon: GeoJSON.Polygon) => void;
	updateDataLayerNames: (names: string[]) => void;
	updateDataLayerValues: (values: (number | string)[]) => void;
	updateHideExclusions: (toggle: boolean) => void;
	updateShowCategoricalValues: (toggle: boolean) => void;
}

type DataAction =
	| {
			type: "plots";
			data: PlotProps[] | null;
	  }
	| { type: "heatmap"; data: HeatmapData[] | null }
	| { type: "heatmap all dates"; data: HeatmapData[] | null }
	| { type: "heatmap loading"; loading: boolean }
	| { type: "trial"; polygon: GeoJSON.Polygon }
	| { type: "data layer"; names: string[] }
	| { type: "data layer values"; values: (number | string)[] }
	| { type: "exclusions"; toggle: boolean }
	| { type: "categorical values"; toggle: boolean };

const dataReducer = (state: DataState, action: DataAction) => {
	switch (action.type) {
		case "plots":
			return { ...state, plotData: action.data };
		case "heatmap":
			return { ...state, heatmapData: action.data };
		case "heatmap all dates":
			return { ...state, heatmapDataAllDates: action.data };
		case "heatmap loading":
			return { ...state, heatmapDataLoading: action.loading };
		case "trial":
			return { ...state, trialPolygon: action.polygon };
		case "data layer":
			return { ...state, dataLayerNames: action.names };
		case "data layer values":
			return { ...state, dataLayerValues: action.values };
		case "exclusions":
			return { ...state, hideExclusions: action.toggle };
		case "categorical values":
			return { ...state, showCategoricalValues: action.toggle };
		default:
			return state;
	}
};

const DataContext = createContext<DataState | null>(null);
const DataDispatchContext = createContext<DataFunctions | null>(null);

const DataProvider = ({ children }: PropsWithChildren) => {
	const [data, dispatch] = useReducer(dataReducer, {
		plotData: null,
		heatmapData: null,
		heatmapDataAllDates: null,
		heatmapDataLoading: false,
		trialPolygon: null,
		dataLayerNames: [],
		dataLayerValues: [],
		hideExclusions: true,
		showCategoricalValues: false,
	});

	const updatePlotData = useCallback((data: PlotProps[] | null) => dispatch({ type: "plots", data }), []);

	const updateHeatMapData = useCallback((data: HeatmapData[] | null) => dispatch({ type: "heatmap", data }), []);

	const updateHeatMapDataAllDates = useCallback(
		(data: HeatmapData[] | null) => dispatch({ type: "heatmap all dates", data }),
		[],
	);

	const updateHeatMapDataLoading = useCallback(
		(loading: boolean) => dispatch({ type: "heatmap loading", loading }),
		[],
	);

	const updateTrialPolygon = useCallback((polygon: GeoJSON.Polygon) => dispatch({ type: "trial", polygon }), []);

	const updateDataLayerNames = useCallback((names: string[]) => dispatch({ type: "data layer", names }), []);

	const updateDataLayerValues = useCallback(
		(values: (number | string)[]) => dispatch({ type: "data layer values", values }),
		[],
	);

	const updateHideExclusions = useCallback((toggle: boolean) => dispatch({ type: "exclusions", toggle }), []);

	const updateShowCategoricalValues = useCallback(
		(toggle: boolean) => dispatch({ type: "categorical values", toggle }),
		[],
	);

	return (
		<DataContext.Provider value={data}>
			<DataDispatchContext.Provider
				value={{
					updatePlotData,
					updateHeatMapData,
					updateHeatMapDataAllDates,
					updateHeatMapDataLoading,
					updateTrialPolygon,
					updateDataLayerNames,
					updateDataLayerValues,
					updateHideExclusions,
					updateShowCategoricalValues,
				}}
			>
				{children}
			</DataDispatchContext.Provider>
		</DataContext.Provider>
	);
};

const useData = () => {
	const context = useContext(DataContext);

	if (context === null) {
		throw new Error("DataContext is not provided");
	}

	return context;
};

const useDataDispatch = () => {
	const context = useContext(DataDispatchContext);

	if (context === null) {
		throw new Error("DataDispatchContext is not provided");
	}

	return context;
};

export { DataProvider, useData, useDataDispatch };
